import { useLoaderData } from '@remix-run/react';
import type { loader } from '~/routes/_app.project.$urlSegment._index';
import { Button } from '@rocco/components/button';
import { useWebsiteTrackingProxy } from '~/modules/tracking/hook/useWebsiteTrackingProxy';
import { useHandleFollow } from '~/modules-rocco/design-board/hook/use-handle-follow';
import { useEnquiryModal } from '~/modules-rocco/enquiry/hooks/use-enquiry-modal';
import { ReadMore } from '@archipro-website/rocco';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogDescription,
} from '@rocco/components/dialog';
import { useState } from 'react';
import { useAppDisplayModeContext } from '@archipro-design/aria';
import { useTracker } from '@archipro-website/tracker';

const Summary = ({ showButtons = true }: { showButtons?: boolean }) => {
    const { project, summary } = useLoaderData<typeof loader>();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const clickProxy = useWebsiteTrackingProxy({
        professionalID: project.Professional.ID,
        professionalTitle: project.Professional.Title,
        itemType: 'Project',
    });

    const { handleFollow, isInDesignBoard } = useHandleFollow();

    const isShortlisted = isInDesignBoard(project.ID, 'ProjectPin');
    const { mobile } = useAppDisplayModeContext();
    const tracker = useTracker();
    const { openEnquiryModal } = useEnquiryModal({
        title: project.Title,
        professional: project.Professional,
        itemType: 'project',
        siteTreeID: project.ID,
        source: 'ProjectDetail',
        ctaLabel: 'Enquire',
    });

    if (!summary) return null;

    return (
        <div className={`px-4.5 py-4.5 md:mb-24 md:px-0 md:pb-0`}>
            {project.Subheading && (
                <h2
                    className="font-base hidden text-[1.25rem] leading-[1.1] tracking-[-0.0465em] md:block md:w-432 md:pt-18 md:text-[3rem] md:font-normal"
                    style={{
                        fontFamily: 'TWK Ghost',
                    }}
                >
                    {project.Subheading}
                </h2>
            )}
            <section className="hidden md:flex md:pt-25">
                <div className="relative top-2 flex w-99 gap-2">
                    {showButtons && (
                        <>
                            <Button
                                className="h-11 w-27 hover:border-gray-900 hover:bg-transparent hover:text-gray-900"
                                color="dark"
                                onClick={() => openEnquiryModal()}
                            >
                                Enquire
                            </Button>
                            <Button
                                className="h-11 min-w-29 border-gray-900 bg-white hover:border-gray-900 hover:bg-gray-900 hover:text-white"
                                color="transparent"
                                border="dark"
                                disabled={isShortlisted}
                                onClick={() =>
                                    handleFollow(
                                        {
                                            id: project.ID,
                                            libraryItemId: project.ID,
                                        },
                                        'ProjectPin'
                                    )
                                }
                            >
                                {isShortlisted ? 'Saved' : 'Save this Project'}
                            </Button>
                        </>
                    )}
                </div>
                <div className="relative ml-75">
                    <div
                        className="flex w-240 gap-24 text-[#232323]"
                        onClick={clickProxy}
                        onContextMenu={clickProxy}
                    >
                        <div className="w-108 overflow-hidden">
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: summary.firstCol,
                                }}
                                className="whitespace-normal text-[1.375rem] leading-[1.32] xl:whitespace-pre-wrap"
                            />
                        </div>
                        <div className="w-108 overflow-hidden">
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: summary.secondCol,
                                }}
                                className="whitespace-normal text-[1.375rem] leading-[1.32] xl:whitespace-pre-wrap"
                            />
                        </div>
                    </div>

                    {summary.isOverflowing && (
                        <Dialog
                            open={isModalOpen}
                            onOpenChange={setIsModalOpen}
                        >
                            <DialogTrigger asChild>
                                <button className="absolute -bottom-12 right-0 flex w-108 justify-start text-[1.375rem] text-foreground hover:underline">
                                    <span className="font-medium text-[#232323]">
                                        Read more
                                    </span>
                                </button>
                            </DialogTrigger>
                            <DialogContent className="max-h-[80vh] overflow-y-auto p-10 text-[1.375rem] text-[#232323] md:max-w-4xl">
                                <DialogHeader>
                                    <DialogTitle className="text-left font-medium text-[#232323]">
                                        {project.Title}
                                    </DialogTitle>
                                    <DialogDescription className="sr-only">
                                        Project summary and details
                                    </DialogDescription>
                                </DialogHeader>
                                <div
                                    className="mt-4 whitespace-pre-wrap leading-[1.32]"
                                    dangerouslySetInnerHTML={{
                                        __html: project.Content,
                                    }}
                                />
                            </DialogContent>
                        </Dialog>
                    )}
                </div>
            </section>
            <div className={`grid grid-cols-1 md:hidden`}>
                <ReadMore
                    maxLines={5}
                    className="text-foreground"
                    buttonClassName={`mt-5 md:mt-7.5 bg-transparent text-foreground underline underline-offset-2 font-medium
                    ${mobile ? 'text-sm' : 'text-base md:text-lg'}`}
                    readMoreText="Read more"
                    readLessText="Read less"
                    alwaysShowButton={true}
                    onToggle={(isClamped) => {
                        if (!isClamped) {
                            // Track the click event when expanding
                            tracker.log('ProjectDetailsExpandClick', {
                                url: new URL(window.location.href),
                                targetTracker: 'archiproTracker',
                            });
                        }
                    }}
                >
                    <div
                        className={`${mobile ? 'text-sm' : 'text-base md:text-lg'}`}
                        style={{ lineHeight: mobile ? '1.3' : '1.5' }}
                        dangerouslySetInnerHTML={{ __html: project.Content }}
                        onClick={clickProxy}
                        onContextMenu={clickProxy}
                    />
                </ReadMore>
                <div className="hidden md:block"></div>{' '}
                {/* Spacer on desktop */}
            </div>
        </div>
    );
};

export default Summary;
